import { useBackgroundTask } from 'components/BackgroundTaskModal/hooks';
import { ICONS } from 'consts/consts';
import { isEqual } from 'lodash';
import React, {
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import {
  FEEDBACK_CONTENT_ADMINABLE,
  FEEDBACK_WITHOUT_REQUESTS_TYPES,
  getFeedbackType,
  getFeedbackVisibilityType,
} from 'utils/models/Feedback';
import { useIsImpersonatinPerson, useIsSuperAdmin } from 'utils/models/User';
import { useCurrentOrganization } from 'utils/util/hooks';
import { formatDateWithUnicode, getDatePart } from 'utils/util/time';
import {
  autoDownloadLink,
  getPrettyDate,
  sanitizeUrlQueryParams,
} from 'utils/util/util';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import FeedbackCard from 'views/Feedback/FeedbackCard';
import { useInfiniteScroll } from 'views/Objectives/ObjectivesOverviewPage/hooks';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import {
  getPeopleAdminableOrSelfQuery,
  getPeopleOrAllQuery,
  INPUT_TYPES,
} from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import {
  FORMAT_AVATAR_GROUP,
  FORMAT_AVATAR_ONLY,
} from 'views/Widgets/People/Filters/common';
import RevealLink from 'views/Widgets/RevealLink';
import DeleteModal from './DeleteModal';
import EditModal from './EditModal';
import FeedbackTypeMultiSelector from './FeedbackTypeMultiSelector';
import MoreActionsButton from './MoreActionsButton';
import UndeleteModal from './UndeleteModal';

const ReceivedFeedbackAndRecognitionAdminPage: FC = () => {
  const { formatMessage, locale } = useIntl();
  const [isEditFeedbackModalOpen, setIsEditFeedbackModalOpen] = useState(false);
  const [isDeleteFeedbackModalOpen, setIsDeleteFeedbackModalOpen] =
    useState(false);
  const [isUndeleteFeedbackModalOpen, setIsUndeleteFeedbackModalOpen] =
    useState(false);
  const [editableFeedbackId, setEditableFeedbackId] = useState<
    number | undefined
  >(undefined);
  const [deletableFeedbackId, setDeletableFeedbackId] = useState<
    number | undefined
  >(undefined);
  const [undeletableFeedbackId, setUndeletableFeedbackId] = useState<
    number | undefined
  >(undefined);
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState<boolean>(false);

  const isSuperAdmin = useIsSuperAdmin();
  const isImpersonatingPerson = useIsImpersonatinPerson();
  const currentOrganization = useCurrentOrganization();

  const handleOnEditClick = useCallback((feedbackId: number) => {
    setEditableFeedbackId(feedbackId);
    setIsEditFeedbackModalOpen(true);
  }, []);

  const handleOnDeleteClick = useCallback((feedbackId: number) => {
    setDeletableFeedbackId(feedbackId);
    setIsDeleteFeedbackModalOpen(true);
  }, []);

  const handleOnUndeleteClick = useCallback((feedbackId: number) => {
    setUndeletableFeedbackId(feedbackId);
    setIsUndeleteFeedbackModalOpen(true);
  }, []);

  const handleToggleEditModal = useCallback(() => {
    setIsEditFeedbackModalOpen(false);
    setEditableFeedbackId(undefined);
  }, []);

  const updateFeedbackOnAccumulatedData = useCallback((editedFeedback) => {
    setAccumulatedData((ad) =>
      ad?.map((feedback) =>
        feedback.id === editedFeedback.id ? editedFeedback : feedback
      )
    );
  }, []);

  const handleCallbackEditModal = useCallback(
    (editedFeedback) => {
      updateFeedbackOnAccumulatedData(editedFeedback);
      handleToggleEditModal();
    },
    [handleToggleEditModal, updateFeedbackOnAccumulatedData]
  );

  const handleToggleDeleteModal = useCallback(() => {
    setIsDeleteFeedbackModalOpen(false);
    setDeletableFeedbackId(undefined);
  }, []);

  const handleCallbackDeleteModal = useCallback(
    (deletedFeedback) => {
      updateFeedbackOnAccumulatedData(deletedFeedback);
      handleToggleDeleteModal();
    },
    [handleToggleDeleteModal, updateFeedbackOnAccumulatedData]
  );

  const handleToggleUndeleteModal = useCallback(() => {
    setIsUndeleteFeedbackModalOpen(false);
    setUndeletableFeedbackId(undefined);
  }, []);

  const handleCallbackUndeleteModal = useCallback(
    (undeletedFeedback) => {
      updateFeedbackOnAccumulatedData(undeletedFeedback);
      handleToggleUndeleteModal();
    },
    [handleToggleUndeleteModal, updateFeedbackOnAccumulatedData]
  );

  const handleFilteringCallback = useCallback(() => {}, []);

  const [currentUrl, setCurrentUrl] = useState<string>('admin/feedback');
  const [currentTotalCount, setCurrentTotalCount] = useState<
    number | undefined
  >(undefined);
  const [currentParams, setCurrentParams] = useState<{
    type?: string;
    author_or_receiver_ids?: string;
  }>({});
  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined);
  const [accumulatedData, setAccumulatedData] = useState<any[] | undefined>(
    undefined
  );
  const [filteringInProgress, setFilteringInProgress] = useState(false);
  const [downloadingCSVInProgress, setDownloadingCSVInProgress] =
    useState(false);

  const callback = useCallback(
    (
      data: { results: any[]; next?: string; count: number } | null,
      error,
      hardError
    ): void => {
      if (!data || error || hardError) {
        console.error('Error fetching feedback data:', error || hardError);
        return;
      }
      setAccumulatedData((prev) => [...(prev ?? []), ...data.results]);
      setNextUrl(
        data.next ? sanitizeUrlQueryParams(data.next, ['page']) : undefined
      );
      setCurrentTotalCount(data.count);
      setFilteringInProgress(false);
      setIsLoadingMoreItems(false);
    },
    []
  );
  const { error } = useConfirmApi<{
    results: any[];
    next?: string;
    count: number;
  }>({
    method: 'GET',
    url: currentUrl,
    params: currentParams,
    callback: callback,
  });

  const [csvTaskDescriptor, setCsvTaskDescriptor] = useState<
    { task_key: string; status: string } | undefined
  >(undefined);

  const handleCsvExportError = useCallback(
    (error: object) => {
      setDownloadingCSVInProgress(false);
      setCsvTaskDescriptor(undefined);
      console.error('Error downloading CSV:', error);
      toast.error(
        formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.received.error_generating_csv',
          defaultMessage: 'Error generating CSV',
        })
      );
    },
    [formatMessage]
  );

  const handleCsvExportCompleted = useCallback(
    (output: BlobPart) => {
      setDownloadingCSVInProgress(false);
      setCsvTaskDescriptor(undefined);
      autoDownloadLink({
        data: output,
        filename: `feedback_${
          currentOrganization?.name ?? 'confirm'
        }_${formatDateWithUnicode(new Date(), 'yyyy_MM_dd_hh_mm_ss')}.csv`,
        mime: 'text/csv',
      });
    },
    [currentOrganization?.name]
  );

  useBackgroundTask({
    taskDescriptor: csvTaskDescriptor,
    onTaskCompleted: handleCsvExportCompleted,
    onError: handleCsvExportError,
  });

  const onCsvExportSubmitted = useCallback(
    (data: { task_key?: string } | null, error) => {
      if (error || !data?.task_key) {
        handleCsvExportError(error);
        return;
      }
      setCsvTaskDescriptor({ task_key: data.task_key, status: 'submitted' });
    },
    [handleCsvExportError]
  );

  const handleOnDownloadCSV = useCallback(() => {
    setDownloadingCSVInProgress(true);
  }, []);

  // This request only enqueues the export; polling delivers the file.
  useConfirmApi({
    method: 'GET',
    url: sanitizeUrlQueryParams(currentUrl),
    params: {
      ...currentParams,
      page: undefined,
      media_type: 'text/csv',
    },
    disabled: !downloadingCSVInProgress || !!csvTaskDescriptor,
    callback: onCsvExportSubmitted,
  });

  const columns = useMemo(() => {
    return [
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.table.column.created_at',
          defaultMessage: 'Date',
        }),
        field: 'created_at',
        format: (date: string) => {
          return getPrettyDate({
            dateString: date,
            locale,
            includeTime: false,
          });
        },
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.table.column.author_person',
          defaultMessage: 'Author',
        }),
        field: 'author_person',
        format: FORMAT_AVATAR_ONLY,
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.table.column.subject_people',
          defaultMessage: 'Recipients',
        }),
        field: 'subject_people',
        format: FORMAT_AVATAR_GROUP,
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.table.column.requester_person',
          defaultMessage: 'Requester',
        }),
        field: 'requester_person',
        className: 'col-auto text-nowrap',
        format: FORMAT_AVATAR_ONLY,
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.table.column.status',
          defaultMessage: 'Status',
        }),
        field: 'deleted_at',
        className: 'col-1',
        format: (deleted_at) => {
          return deleted_at
            ? formatMessage({
                id: 'app.views.administration.content_administration.feedback_recognition.table.column.deleted_at.value.deleted',
                defaultMessage: 'Deleted',
              })
            : formatMessage({
                id: 'app.views.administration.content_administration.feedback_recognition.table.column.deleted_at.value.live',
                defaultMessage: 'Live',
              });
        },
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.table.column.type',
          defaultMessage: 'Type',
        }),
        field: 'type',
        className: 'col-auto text-nowrap',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.table.column.body',
          defaultMessage: 'Body',
        }),
        field: 'body',
        className: 'col-auto',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.table.column.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-1',
        sortable: false,
      },
    ];
  }, [formatMessage, locale]);

  const rows = useMemo(() => {
    if (!accumulatedData) {
      return undefined;
    }
    return accumulatedData.map((feedback) => {
      const type = getFeedbackType(feedback, formatMessage);
      const visibility = getFeedbackVisibilityType(feedback, formatMessage);
      return {
        id: feedback.id,
        created_at: feedback.created_at,
        type: type?.title,
        // @ts-expect-error
        visibility: visibility.name,
        author_person: feedback.author_person,
        subject_people: feedback.subject_people,
        requester_person: feedback?.feedback_request?.author_person,
        body: (
          <FeedbackCard
            feedback={feedback}
            containerClassName="p-0 m-0"
            containerBodyClassName=""
          />
        ),
        actions: (
          <MoreActionsButton
            feedback={feedback}
            onEditClick={handleOnEditClick}
            onDeleteClick={handleOnDeleteClick}
            onUndeleteClick={handleOnUndeleteClick}
          />
        ),
        deleted_at: feedback.deleted_at,
      };
    });
  }, [
    accumulatedData,
    formatMessage,
    handleOnDeleteClick,
    handleOnEditClick,
    handleOnUndeleteClick,
  ]);

  const renderInputs = useCallback(
    ([types, sender, startDate, endDate]: ReactNode[]) => {
      return (
        <Row>
          <Col className="col-4">
            <Row>
              <Col className="ps-0">{sender}</Col>
              <Col className="col-auto">
                <div className="text-muted mt-2 pt-1">
                  <i className="fe fe-filter me-2" />
                </div>
              </Col>
            </Row>
            <Row>
              <RevealLink
                className="ps-0 ms-0 mt-n3 mb-2 pb-2 pt-0"
                text={formatMessage({
                  id: 'app.views.administration.content_administration.feedback_recognition.search.reveal_link.additional_search_options',
                  defaultMessage: 'Additional search options...',
                })}
              >
                <Col className="ps-0">{startDate}</Col>
                <Col className="ps-0">{endDate}</Col>
              </RevealLink>
            </Row>
          </Col>
          <Col className="col-5">{types}</Col>
          <Col className="col-3">
            {!!currentTotalCount && (
              <div className="float-right">
                <Button
                  onClick={handleOnDownloadCSV}
                  className="btn btn-sm btn-light float-end"
                >
                  <i className={ICONS.EXPORT + ' me-2'} />
                  <FormattedMessage
                    id="app.views.administration.content_administration.feedback_and_recognition_admin.page.export_to_csv"
                    defaultMessage="Export {count, plural, one {# item} other {# items}} to CSV"
                    values={{ count: currentTotalCount }}
                  />
                </Button>
              </div>
            )}
          </Col>
        </Row>
      );
    },
    [currentTotalCount, formatMessage, handleOnDownloadCSV]
  );

  const handleOnChangeFilter = useCallback(
    (object) => {
      const { types, author_or_receiver, start_date, end_date } = object;
      const params = {
        types,
        author_or_receiver_ids: (author_or_receiver ?? [])
          .map((it) => it.id)
          .join(','),
        start_date: start_date ? getDatePart(start_date) : undefined,
        end_date: end_date ? getDatePart(end_date) : undefined,
      };
      if (!isEqual(currentParams, params)) {
        setFilteringInProgress(true);
        setIsLoadingMoreItems(false);
        setAccumulatedData(undefined);
        setCurrentParams(params);
        setCurrentUrl('admin/feedback');
        setNextUrl(undefined);
      }
    },
    [currentParams]
  );

  const adminablePersonElasticSearchOptions = useMemo(() => {
    return {
      getQuery: isSuperAdmin
        ? getPeopleOrAllQuery
        : getPeopleAdminableOrSelfQuery,
    };
  }, [isSuperAdmin]);

  const visibleFeedbackTypes = useMemo(() => {
    return isSuperAdmin && !isImpersonatingPerson
      ? FEEDBACK_WITHOUT_REQUESTS_TYPES(formatMessage)
      : FEEDBACK_CONTENT_ADMINABLE(formatMessage);
  }, [isSuperAdmin, isImpersonatingPerson, formatMessage]);

  const actions = useMemo(() => {
    return (
      <Card>
        <CardBody className="pt-4 pb-0 mb-0">
          <ValidatedForm
            renderInputs={renderInputs}
            buttonColor="light"
            hideSubmitButton={true}
            disableUnsavedChangesPrompt={true}
            onChange={handleOnChangeFilter}
            callback={handleFilteringCallback}
            inputs={[
              {
                name: 'types',
                type: INPUT_TYPES.CUSTOM_COMPONENT,
                component: FeedbackTypeMultiSelector,
                types: visibleFeedbackTypes,
              },
              {
                name: 'author_or_receiver',
                type: INPUT_TYPES.PEOPLE_EDITOR,
                allowSelf: true,
                placeholder: formatMessage({
                  id: 'app.views.administration.content_administration.feedback_recognition.search_by_author_or_receiver',
                  defaultMessage: 'Search by author or receiver',
                }),
                elasticsearchOptions: adminablePersonElasticSearchOptions,
              },
              {
                name: 'start_date',
                helperText: formatMessage({
                  id: 'app.views.administration.content_administration.feedback_recognition.search.start_date.helper_text',
                  defaultMessage: 'Start date',
                }),
                type: INPUT_TYPES.DATE_PICKER,
              },
              {
                name: 'end_date',
                helperText: formatMessage({
                  id: 'app.views.administration.content_administration.feedback_recognition.search.end_date.helper_text',
                  defaultMessage: 'End date',
                }),
                type: INPUT_TYPES.DATE_PICKER,
              },
            ]}
          />
        </CardBody>
      </Card>
    );
  }, [
    renderInputs,
    handleOnChangeFilter,
    handleFilteringCallback,
    formatMessage,
    adminablePersonElasticSearchOptions,
    visibleFeedbackTypes,
  ]);

  const loadMoreRef = useRef(null);

  const handleLoadMore = useCallback((nextUrl) => {
    setCurrentUrl(nextUrl);
    setIsLoadingMoreItems(true);
  }, []);

  useInfiniteScroll(
    loadMoreRef,
    useCallback(() => {
      if (nextUrl) {
        handleLoadMore(nextUrl);
      }
    }, [nextUrl, handleLoadMore])
  );

  if (error) {
    return <SimpleErrorPage error={error} />;
  }

  return (
    <>
      <Loading
        active={filteringInProgress || !rows}
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.loading.message.loading_data',
          defaultMessage: 'Loading data...',
        })}
      />
      <Loading
        active={downloadingCSVInProgress}
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.feedback_recognition.loading.message.downloading_csv',
          defaultMessage: 'Downloading CSV, please wait...',
        })}
      />
      {editableFeedbackId && (
        <EditModal
          isOpen={isEditFeedbackModalOpen}
          feedbackId={editableFeedbackId}
          toggle={handleToggleEditModal}
          callback={handleCallbackEditModal}
        />
      )}
      {deletableFeedbackId && (
        <DeleteModal
          isOpen={isDeleteFeedbackModalOpen}
          feedbackId={deletableFeedbackId}
          onClosed={handleToggleDeleteModal}
          onDelete={handleCallbackDeleteModal}
        />
      )}
      {undeletableFeedbackId && (
        <UndeleteModal
          isOpen={isUndeleteFeedbackModalOpen}
          feedbackId={undeletableFeedbackId}
          onClosed={handleToggleUndeleteModal}
          onUndelete={handleCallbackUndeleteModal}
        />
      )}
      {actions}
      {!!rows && (
        <FilterablePeopleTable
          columns={columns}
          rows={rows}
          hideFilters={true}
          hideExportButton={true}
        />
      )}
      <div ref={loadMoreRef} />
      {isLoadingMoreItems && <Loading />}
    </>
  );
};

export default React.memo(ReceivedFeedbackAndRecognitionAdminPage);
